import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import useLocale from '../contexts/LocaleContext';
import type { QuestionnaireDocumentDto } from '../types/api';
import { Dialog, DialogContent, DialogTitle } from './ui/Dialog';

interface FullScreenPdfViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: QuestionnaireDocumentDto[];
  title?: string;
}

const FullScreenPdfViewer: React.FC<FullScreenPdfViewerProps> = ({ open, onOpenChange, documents, title }) => {
  const { dictionary } = useLocale();
  const [selectedId, setSelectedId] = useState<string | null>(documents[0]?.id ?? null);

  useEffect(() => {
    if (open) {
      setSelectedId(documents[0]?.id ?? null);
    }
  }, [open, documents]);

  const selectedDocument = documents.find(doc => doc.id === selectedId) ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-none w-screen h-screen sm:max-w-none translate-x-0 translate-y-0 top-0 left-0 rounded-none p-0 gap-0 flex flex-col'>
        <DialogTitle className='sr-only'>{title || dictionary['questionnaire.reference_documents']}</DialogTitle>
        <div className='flex items-center gap-3 border-b border-gray-200 px-4 py-3 shrink-0'>
          <FileText className='h-5 w-5 text-primary shrink-0' />
          <span className='font-semibold text-gray-900 truncate'>{title || dictionary['questionnaire.reference_documents']}</span>
          {documents.length > 1 && (
            <select
              className='ml-auto border border-gray-200 rounded-md text-sm px-2 py-1 max-w-[60%]'
              value={selectedId ?? ''}
              onChange={e => setSelectedId(e.target.value)}
            >
              {documents.map(doc => (
                <option key={doc.id} value={doc.id}>
                  {doc.label || doc.asset.originalName}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className='flex-1 min-h-0 bg-gray-100'>
          {selectedDocument ? (
            <iframe
              key={selectedDocument.id}
              src={selectedDocument.asset.url}
              title={selectedDocument.label || selectedDocument.asset.originalName}
              className='w-full h-full border-0'
            />
          ) : (
            <div className='flex items-center justify-center h-full text-gray-500 text-sm px-4 text-center'>
              {dictionary['questionnaire.no_documents']}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FullScreenPdfViewer;
